import { FLAGS } from '@gozoki/feature-flags'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Inbox, LayoutDashboard, Settings, Star, Users } from 'lucide-react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { AppTabs, type AppTab } from '~/components/new/app-ui/app-tabs'
import { resolveTabs } from '~/components/new/app-ui/app-tabs-state'
import { PeriodSelect } from '~/components/new/app-ui/period-select'
import { Title } from '~/components/new/app-ui/title'
import { FeatureRouteGuard } from '~/components/route-guards'
import { ClientsTab } from '~/components/routes/service-client/client-profile/clients-tab'
import { ReviewsTab } from '~/components/routes/service-client/reviews-tab'
import { InboxTab } from '~/components/routes/service-client/inbox-tab'
import { useInboxUnreadCount } from '~/components/routes/service-client/service-client-api'
import {
  useGoogleReviewsCount,
  useReviewsAccess,
} from '~/components/routes/service-client/service-client-reviews-api'
import {
  formatPeriodRange,
  serviceClientSearchSchema,
  useServiceClientFilters,
} from '~/components/routes/service-client/service-client-filters'
import { ServiceClientSettingsDialog } from '~/components/routes/service-client/service-client-settings-dialog'
import { ServiceClientStoreFilter } from '~/components/routes/service-client/service-client-store-filter'
import {
  SERVICE_CLIENT_CUSTOMER_READ,
  SERVICE_CLIENT_TICKET_READ,
} from '~/components/routes/service-client/service-client-rights'
import { Button } from '~/components/ui/button'
import { TabsContent } from '~/components/ui/tabs'
import { cn } from '~/lib/utils'
import { usePermissionGuard, withRoutePermissions } from '~/utils/rbac'

type ServiceClientTab = 'dashboard' | 'inbox' | 'clients' | 'reviews'

export const Route = createFileRoute('/_authenticated/service-client/')({
  staticData: {
    permission: SERVICE_CLIENT_TICKET_READ,
  },
  component: ServiceClientRoute,
  validateSearch: serviceClientSearchSchema,
})

withRoutePermissions(Route)

/** Page Service client : flag `service-client-sidebar`, puis lecture des tickets. */
function ServiceClientRoute() {
  return (
    <FeatureRouteGuard flag={FLAGS.serviceClientSidebar}>
      <ServiceClientPage />
    </FeatureRouteGuard>
  )
}

/** Pastille compteur dans un onglet ; teinte selon que l'onglet est actif (fond vert) ou non. */
function TabCountBadge({ count, active }: { count: number; active: boolean }) {
  return (
    <span
      className={cn(
        'ml-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-primary-800',
        active ? 'bg-white' : 'bg-primary-50',
      )}
    >
      {count}
    </span>
  )
}

function ServiceClientPage() {
  const { tab, clientView } = Route.useSearch()
  const navigate = Route.useNavigate()

  // Badge d'onglet = non lus des filtres globaux (période et boutiques, requête dédiée) : il suit la
  // barre d'onglets comme la liste, mais pas les filtres propres à la liste, contrairement au
  // compteur de la barre d'outils.
  const { search, period } = useServiceClientFilters()
  const unreadCount = useInboxUnreadCount(search, period)
  // Badge « Avis Google » : avis sans réponse, toutes dates et toutes boutiques.
  const reviewsAccess = useReviewsAccess()
  const reviewsCount = useGoogleReviewsCount({}, reviewsAccess.canRead)
  // L'onglet Clients lit les routes `customers.*` (droit de l'écran Clients), pas celles des tickets.
  const customersGuard = usePermissionGuard(SERVICE_CLIENT_CUSTOMER_READ)

  const requestedTab: ServiceClientTab = tab ?? 'inbox'
  const setTab = (next: ServiceClientTab) =>
    navigate({ search: (prev) => ({ ...prev, tab: next }) })

  // Onglet Clients désactivé sans le droit (jamais masqué), onglet actif replié sur le premier
  // ouvert : même règle que Logistique (`resolveTabs`). Les autres onglets portent le droit de la route.
  const tabValues: { value: ServiceClientTab }[] = [
    { value: 'dashboard' },
    { value: 'inbox' },
    { value: 'clients' },
    { value: 'reviews' },
  ]
  const resolved = resolveTabs(
    tabValues,
    tabValues.map((item) => (item.value === 'clients' ? customersGuard : { authorized: true })),
    requestedTab,
  )
  // Repli de `resolveTabs` = premier onglet ouvert (Tableau de bord, pas encore livré) : la page
  // atterrit plutôt sur la Boîte de réception, son onglet par défaut.
  const activeTab: ServiceClientTab = resolved.activeTab === requestedTab ? requestedTab : 'inbox'
  const resolvedTabs = resolved.tabs
  const tabLabels: Record<ServiceClientTab, Omit<AppTab<ServiceClientTab>, 'value'>> = {
    dashboard: { label: 'Tableau de bord', icon: LayoutDashboard },
    inbox: {
      label: (
        <span className="inline-flex items-center">
          Boîte de réception
          <TabCountBadge count={unreadCount} active={activeTab === 'inbox'} />
        </span>
      ),
      icon: Inbox,
    },
    clients: { label: 'Clients', icon: Users },
    reviews: {
      label: (
        <span className="inline-flex items-center">
          Avis Google
          {reviewsAccess.canRead ? (
            <TabCountBadge count={reviewsCount?.unanswered ?? 0} active={activeTab === 'reviews'} />
          ) : null}
        </span>
      ),
      icon: Star,
    },
  }
  const tabs: AppTab<ServiceClientTab>[] = resolvedTabs.map((item) => ({
    ...item,
    ...tabLabels[item.value],
  }))

  return (
    <>
      <AppHeader>
        <Title>Service client</Title>
      </AppHeader>

      <AppContainer>
        <AppTabs
          tabs={tabs}
          value={activeTab}
          onTabChange={setTab}
          variant="pill"
          // Période et boutiques ne filtrent pas la fiche client : masquées sur l'onglet Clients.
          // Masqués sur la fiche client (Figma 14502:69901), présents sur ses tickets (14502:70126).
          actions={
            activeTab === 'clients' && clientView !== 'tickets' ? undefined : (
              <ServiceClientTopControls />
            )
          }
        >
          <TabsContent value="dashboard">
            <ComingSoon />
          </TabsContent>
          <TabsContent value="inbox">
            <InboxTab />
          </TabsContent>
          <TabsContent value="clients">
            {customersGuard.authorized ? <ClientsTab /> : null}
          </TabsContent>
          <TabsContent value="reviews">
            {reviewsAccess.canRead ? <ReviewsTab /> : <ComingSoon />}
          </TabsContent>
        </AppTabs>
      </AppContainer>
    </>
  )
}

/**
 * Contrôles globaux de la barre d'onglets, partagés entre onglets et stockés dans l'URL : période
 * (défaut 7 j) et boutiques. La roue « Réglages » ouvre les réglages du module ; elle reste
 * désactivée sans la lecture des avis Google (le seul réglage porte sur leurs tickets).
 */
function ServiceClientTopControls() {
  const { period, setSearch } = useServiceClientFilters()
  const { canRead } = useReviewsAccess()
  const [settingsOpen, setSettingsOpen] = useState(false)
  return (
    <>
      <div className="flex flex-col items-start gap-0.5">
        <PeriodSelect period={period} onPeriodChange={(next) => setSearch({ period: next })} />
        <span className="px-1 text-xs text-sage-500">{formatPeriodRange(period)}</span>
      </div>
      <ServiceClientStoreFilter />
      <Button
        size="icon"
        disabled={!canRead}
        aria-label="Réglages"
        className="size-10 bg-primary-500 text-white"
        onClick={() => setSettingsOpen(true)}
      >
        <Settings className="size-5" />
      </Button>
      <ServiceClientSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  )
}

function ComingSoon() {
  return <p className="mt-6 text-sm text-muted-foreground">Bientôt disponible.</p>
}
